import { LayoutGrid, LayoutList } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CircleLoader } from 'common-src/components/base';
import { ColorsNew } from 'common-src/styles';

import { getCareOrchestrationState, saveCareOrchestrationState } from 'src/features/tabsState';
import useCustomSelector from 'src/hooks/useCustomSelector';

import { departmentsInScope, hasActiveScopeFilters } from '../../scope';
import {
  deferTask,
  dismissVisitTask,
  fetchPatientsVisitHistory,
  fetchTaskQueueData,
  reopenTask,
} from '../../services/careOrchestrationApi';
import ScopeFilterGroup from '../ScopeFilterGroup';
import { BriefRow, SmallCaps, TopicBand } from '../Visit';
import OpenButtons from './OpenButtons';
import StatusPills from './StatusPills';
import {
  anchorShort,
  bookableCodes,
  DEFAULT_STATES,
  formatShortDate,
  groupVisitRows,
  memberMeta,
  STATE_CHIPS,
  toVisitRow,
  VISIT_CHIPS,
  VISITS_DUE_EVIDENCE_TYPE,
} from './visitsDue';
import VisitsDueDetail from './VisitsDueDetail';
import styles from './VisitsDueView.module.scss';

const BOOKED_THIS_WEEK_DAYS = 7;

const toggle = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const LAYOUTS = [
  { key: 'list', label: 'List', Icon: LayoutList },
  { key: 'cards', label: 'Cards', Icon: LayoutGrid },
];

const VisitsDueView = ({
  organizationScope = null,
  departments = [],
  scopeFilters,
  onChangeScopeFilters,
  guideEnabled = false,
  onToggleGuide,
  isScopePaused = false,
  onToggleScopePause,
  isPinnedToCurrentScope = false,
  canResetToPinnedScope = false,
  showScopeUtilities = false,
  onToggleScopePin,
  onResetScopeToPinned,
}) => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [states, setStates] = useState(DEFAULT_STATES);
  const [visits, setVisits] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);
  const [deferringId, setDeferringId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const dispatch = useDispatch();
  const savedState = useCustomSelector((state) => getCareOrchestrationState(state));
  const layout = savedState?.visitsDueLayout === 'cards' ? 'cards' : 'list';

  const scopedDepartmentIds = useMemo(
    () =>
      departmentsInScope(departments, scopeFilters, guideEnabled).map(
        (department) => `${department.id}`,
      ),
    [departments, scopeFilters, guideEnabled],
  );
  const isAllScope = isScopePaused || (!hasActiveScopeFilters(scopeFilters) && !guideEnabled);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchTaskQueueData({
      statusScope: 'open',
      organizationScope,
      departmentAthenaIds: isAllScope ? null : scopedDepartmentIds,
      evidenceType: VISITS_DUE_EVIDENCE_TYPE,
    })
      .then(async ({ taskQueueRows }) => {
        const rawRows = taskQueueRows || [];
        const patientIds = [...new Set(rawRows.map((row) => row.patientId))];
        const historyByPatientId = patientIds.length
          ? ((await fetchPatientsVisitHistory(patientIds).catch(() => null))?.data ?? {})
          : {};
        if (cancelled) return;
        setRows(
          rawRows.map((row) => {
            const ruleId = row.evidence?.sourceRef;
            const codes = bookableCodes(ruleId);
            const history = historyByPatientId[row.patientId] ?? [];
            const bookedVisit = history.find((visit) => visit.booked && codes.includes(visit.code));
            return toVisitRow({ ...row, bookedVisit, history });
          }),
        );
      })
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [organizationScope, isAllScope, scopedDepartmentIds]);

  const groups = useMemo(() => groupVisitRows(rows, states, visits), [rows, states, visits]);
  const selectedRow =
    groups.flatMap((group) => group.rows).find((row) => row.id === selectedId) ??
    groups[0]?.rows[0] ??
    null;
  const memberCount = new Set(rows.map((row) => row.patientId)).size;
  const overdue = rows.filter((row) => row.dueState === 'overdue').length;
  const due = rows.filter((row) => row.dueState === 'due').length;
  const deferred = rows.filter((row) => row.dueState === 'deferred').length;
  const bookedThisWeekCutoff = new Date(Date.now() + BOOKED_THIS_WEEK_DAYS * 24 * 60 * 60 * 1000);
  const bookedThisWeek = rows.filter(
    (row) => row.dueState === 'booked' && new Date(row.bookedVisit.date) <= bookedThisWeekCutoff,
  ).length;

  const stats = [
    { key: 'overdue', label: 'Overdue', value: overdue, tone: overdue > 0 ? 'red' : null },
    { key: 'due', label: 'Due', value: due, tone: due > 0 ? 'yellow' : null },
    { key: 'deferred', label: 'Deferred', value: deferred, tone: null },
    {
      key: 'booked',
      label: 'Booked this week',
      value: bookedThisWeek,
      tone: bookedThisWeek > 0 ? 'green' : null,
    },
  ];

  const selectLayout = (next) => {
    dispatch(saveCareOrchestrationState({ visitsDueLayout: next }));
  };

  const applyUpdate = (rowId, updated) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? toVisitRow({
              ...r,
              heldUntil: updated.heldUntil,
              dismissedAt: updated.dismissedAt,
              deferredReason: updated.deferredReason,
              visitActivity: updated.visitActivity,
            })
          : r,
      ),
    );

  const handleDismiss = async (row, reason, note) => {
    if (dismissingId) return;
    setDismissingId(row.id);
    try {
      applyUpdate(row.id, await dismissVisitTask(row.id, reason, note));
    } finally {
      setDismissingId(null);
    }
  };

  const handleDefer = async (row, reevalOn, reason, note) => {
    if (deferringId) return;
    setDeferringId(row.id);
    try {
      const heldUntil = new Date(`${reevalOn}T00:00:00`).toISOString();
      applyUpdate(row.id, await deferTask(row.id, heldUntil, reason, note));
    } finally {
      setDeferringId(null);
    }
  };

  const handleReopen = async (row) => {
    if (reopeningId) return;
    setReopeningId(row.id);
    try {
      applyUpdate(row.id, await reopenTask(row.id));
    } finally {
      setReopeningId(null);
    }
  };

  const listRef = useRef(null);
  const rowOrder = groups.flatMap((group) => group.rows.map((row) => row.id));

  const handleListKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const at = selectedRow ? rowOrder.indexOf(selectedRow.id) : -1;
    const next =
      rowOrder[
        Math.min(rowOrder.length - 1, Math.max(0, at + (event.key === 'ArrowDown' ? 1 : -1)))
      ];
    if (!next) return;
    event.preventDefault();
    setSelectedId(next);
    listRef.current?.querySelector(`[data-row-id="${next}"]`)?.focus();
  };

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <ScopeFilterGroup
          departments={departments}
          filters={scopeFilters}
          onChangeFilters={onChangeScopeFilters}
          guideEnabled={guideEnabled}
          onToggleGuide={onToggleGuide}
          showUtilities={showScopeUtilities}
          isPinned={isPinnedToCurrentScope}
          onTogglePin={onToggleScopePin}
          canReset={canResetToPinnedScope}
          onReset={onResetScopeToPinned}
          isPaused={isScopePaused}
          onTogglePause={onToggleScopePause}
        />
        <span className={styles.readout}>
          {isLoading
            ? 'Loading…'
            : `${memberCount} ${memberCount === 1 ? 'member' : 'members'} due a visit`}
        </span>
      </div>

      <div className={styles.statsRow}>
        {stats.map((stat) => (
          <div key={stat.key} className={styles.statCard}>
            <div className={styles.statLabel}>{stat.label}</div>
            <span
              className={`${styles.statValue} ${stat.tone ? styles[`statValue${stat.tone}`] : ''}`}
            >
              {stat.value}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.layoutGrid}>
        <div className={styles.listColumn}>
          <div className={styles.chipsRow}>
            {STATE_CHIPS.map((chip) => (
              <button
                key={chip.key}
                type="button"
                aria-pressed={states.includes(chip.key)}
                className={`${styles.chip} ${states.includes(chip.key) ? styles.chipOn : ''}`}
                onClick={() => setStates((prev) => toggle(prev, chip.key))}
              >
                {chip.label}
              </button>
            ))}
            <span className={styles.chipDivider} aria-hidden="true" />
            {VISIT_CHIPS.map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={visits.includes(code)}
                className={`${styles.chip} ${styles.chipMono} ${visits.includes(code) ? styles.chipOn : ''}`}
                onClick={() => setVisits((prev) => toggle(prev, code))}
              >
                {code}
              </button>
            ))}
            <div className={styles.layoutToggle} role="group" aria-label="Layout">
              {LAYOUTS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={layout === key}
                  className={`${styles.layoutButton} ${layout === key ? styles.layoutButtonOn : ''}`}
                  onClick={() => selectLayout(key)}
                >
                  <Icon size={14} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className={styles.loaderWrap}>
              <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={12} />
            </div>
          ) : groups.length === 0 ? (
            <div className={styles.emptyState}>No members are due a visit in this scope.</div>
          ) : layout === 'list' ? (
            <div
              ref={listRef}
              role="presentation"
              className={styles.listCard}
              onKeyDown={handleListKeyDown}
            >
              <div className={styles.listHeader}>
                <div className={`${styles.columns} ${styles.headerColumns}`}>
                  {['Member', 'Status', 'Due', 'Anchor', 'Clinician · Site'].map((label) => (
                    <SmallCaps key={label}>{label}</SmallCaps>
                  ))}
                </div>
                <SmallCaps className={styles.openHeader}>Open</SmallCaps>
              </div>
              {groups.map((group) => (
                <section key={group.ruleId} aria-label={group.label}>
                  <TopicBand
                    density="group"
                    headingLevel="h3"
                    label={group.label}
                    meta={group.rows.length}
                    className={styles.groupBand}
                  />
                  {group.rows.map((row) => (
                    <div
                      key={row.id}
                      className={`${styles.listRow} ${row.id === selectedRow?.id ? styles.listRowOn : ''}`}
                    >
                      <button
                        type="button"
                        aria-pressed={row.id === selectedRow?.id}
                        data-row-id={row.id}
                        className={`${styles.columns} ${styles.rowButton}`}
                        onClick={() => setSelectedId(row.id)}
                      >
                        <span className={styles.cellName}>
                          {row.patient?.name ?? row.patientId}
                        </span>
                        <span className={styles.cellStatus}>
                          <StatusPills row={row} />
                        </span>
                        <span className={styles.cellMono}>
                          {row.dueState === 'held' ? '—' : formatShortDate(row.due)}
                        </span>
                        <span className={styles.cellAnchor} title={anchorShort(row)}>
                          {anchorShort(row)}
                        </span>
                        <span className={styles.cellMeta}>{memberMeta(row)}</span>
                      </button>
                      <span className={styles.rowLinks}>
                        <OpenButtons row={row} />
                      </span>
                    </div>
                  ))}
                </section>
              ))}
            </div>
          ) : (
            <div className={styles.cardsWrap}>
              {groups.map((group) => (
                <BriefRow key={group.ruleId} label={group.label} meta={group.rows.length} flush>
                  {group.rows.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      aria-pressed={row.id === selectedRow?.id}
                      className={`${styles.gapCard} ${row.id === selectedRow?.id ? styles.gapCardOn : ''}`}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <span className={styles.gapCardMain}>
                        <span className={styles.rowName}>{row.patient?.name ?? row.patientId}</span>
                        <span className={styles.gapCardTitle}>{row.title}</span>
                        {row.evidence?.summary && (
                          <span className={styles.gapCardEvidence}>{row.evidence.summary}</span>
                        )}
                        <span className={styles.rowMeta}>{memberMeta(row)}</span>
                      </span>
                      <span className={styles.gapCardSide}>
                        <StatusPills row={row} />
                      </span>
                    </button>
                  ))}
                </BriefRow>
              ))}
            </div>
          )}
        </div>

        <div className={styles.railColumn}>
          {selectedRow ? (
            <VisitsDueDetail
              row={selectedRow}
              onDismiss={handleDismiss}
              isDismissing={dismissingId === selectedRow.id}
              onDefer={handleDefer}
              isDeferring={deferringId === selectedRow.id}
              onReopen={handleReopen}
              isReopening={reopeningId === selectedRow.id}
            />
          ) : (
            <div className={styles.railEmpty}>Select a row to see why it is due.</div>
          )}
        </div>
      </div>
    </div>
  );
};

VisitsDueView.propTypes = {
  organizationScope: PropTypes.string,
  departments: PropTypes.array,
  scopeFilters: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.string),
    counties: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    departments: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onChangeScopeFilters: PropTypes.func.isRequired,
  guideEnabled: PropTypes.bool,
  onToggleGuide: PropTypes.func,
  isScopePaused: PropTypes.bool,
  onToggleScopePause: PropTypes.func,
  isPinnedToCurrentScope: PropTypes.bool,
  canResetToPinnedScope: PropTypes.bool,
  showScopeUtilities: PropTypes.bool,
  onToggleScopePin: PropTypes.func,
  onResetScopeToPinned: PropTypes.func,
};

export default VisitsDueView;
